import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { AddressSelector, CouponInput } from '../components/checkout';
import { shippingService } from '../services/shippingService';
import { useRazorpay } from '../hooks/useRazorpay';
import { PENDING_CART_ITEM_KEY } from '../constants/storageKeys';
import './Checkout.css';

export default function Checkout() {
  const { items, getTotalPrice, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { initiatePayment, loading: razorpayLoading, error: razorpayError } = useRazorpay();

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [shippingRates, setShippingRates] = useState({});
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shippingError, setShippingError] = useState(null);
  const [couponData, setCouponData] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (items.length === 0) {
      const pendingItem = sessionStorage.getItem(PENDING_CART_ITEM_KEY);
      if (!pendingItem) {
        navigate('/cart');
      }
    }
  }, [items.length, navigate]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await shippingService.getProviders();
        if (response.success) {
          setAvailableProviders(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      } finally {
        setProvidersLoading(false);
      }
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    if (selectedAddress?.pincode && availableProviders.length > 0) {
      fetchShippingRates(selectedAddress.pincode);
    } else {
      setShippingRates({});
      setSelectedProvider(null);
    }
  }, [selectedAddress, availableProviders]);

  useEffect(() => {
    if (razorpayError) {
      setError(razorpayError);
      setPaymentLoading(false);
    }
  }, [razorpayError]);

  const fetchShippingRates = async (pincode) => {
    setShippingLoading(true);
    setShippingError(null);
    try {
      const rates = await shippingService.getRatesForAllProviders(availableProviders, pincode);
      setShippingRates(rates);

      const serviceableProviders = Object.values(rates).filter(r => r.serviceable);
      if (serviceableProviders.length > 0) {
        setSelectedProvider(serviceableProviders[0].providerCode);
      } else {
        setSelectedProvider(null);
        setShippingError('Delivery not available to this pincode');
      }
    } catch (err) {
      console.error('Shipping calculation error:', err);
      setShippingError('Failed to calculate shipping');
    } finally {
      setShippingLoading(false);
    }
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setError(null);
  };

  const handleProviderSelect = (providerCode) => {
    setSelectedProvider(providerCode);
  };

  const handleCouponApplied = (data) => {
    setCouponData(data);
  };

  const handleCouponRemoved = () => {
    setCouponData(null);
  };

  const handlePayment = () => {
    if (!selectedAddress) {
      setError('Please select a delivery address');
      return;
    }
    if (!selectedProvider) {
      setError('Please select a shipping provider');
      return;
    }

    setPaymentLoading(true);
    setError(null);

    initiatePayment({
      addressId: selectedAddress.id,
      deliveryPartner: selectedProvider,
      deliveryPartnerTat: selectedRate?.estimatedDays,
      shippingCost: shippingCost,
      couponReservationId: couponData?.reservationId || null,
      userInfo: {
        name: selectedAddress.fullName,
        email: user?.email,
        phone: selectedAddress.phoneNumber
      },
      onSuccess: (data) => {
        clearCart();
        navigate(`/order-success?order=${data.orderNumber}`);
      },
      onFailure: (err) => {
        if (!err.cancelled) {
          setError(err.message || 'Payment failed. Please try again.');
        }
        setPaymentLoading(false);
      }
    });
  };

  const subtotal = getTotalPrice();
  const selectedRate = selectedProvider ? shippingRates[selectedProvider] : null;
  const shippingCost = selectedRate?.shippingCost || 0;
  const shippingDiscount = couponData?.discountBreakdown?.shippingDiscount
    ? parseFloat(couponData.discountBreakdown.shippingDiscount) : 0;
  const subtotalDiscount = couponData?.discountBreakdown?.subtotalDiscount
    ? parseFloat(couponData.discountBreakdown.subtotalDiscount) : 0;
  const couponFreeDelivery = shippingDiscount > 0 && shippingDiscount >= shippingCost;
  const effectiveShipping = couponFreeDelivery ? 0 : shippingCost;
  const discount = subtotalDiscount;
  const total = subtotal + effectiveShipping - subtotalDiscount;

  const isLoading = paymentLoading || razorpayLoading;
  // Ensure checkout is only enabled when shipping rates are loaded and a valid rate is selected
  const canProceed = selectedAddress && selectedProvider && selectedRate && !shippingLoading && items.length > 0;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <motion.h1
          className="checkout-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Checkout
        </motion.h1>

        {error && (
          <motion.div
            className="checkout-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <div className="checkout-content">
          <div className="checkout-form-section">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <AddressSelector
                onAddressSelect={handleAddressSelect}
                selectedAddressId={selectedAddress?.id}
              />
            </motion.div>

            {selectedAddress && (
              <motion.div
                className="form-section shipping-section"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="section-title">Shipping Partner</h2>

                {shippingLoading || providersLoading ? (
                  <div className="shipping-loading">
                    <div className="loading-spinner-small"></div>
                    <span>Checking delivery options...</span>
                  </div>
                ) : shippingError ? (
                  <div className="shipping-error">
                    <span className="error-icon">!</span>
                    <span>{shippingError}</span>
                  </div>
                ) : (
                  <div className="shipping-options">
                    {availableProviders.map((provider) => {
                      const rate = shippingRates[provider.code];
                      if (!rate) return null;

                      const isSelected = selectedProvider === provider.code;
                      const isServiceable = rate.serviceable;

                      return (
                        <div
                          key={provider.code}
                          className={`shipping-card ${isSelected ? 'selected' : ''} ${!isServiceable ? 'disabled' : ''}`}
                          onClick={() => isServiceable && handleProviderSelect(provider.code)}
                        >
                          <div className="shipping-card-left">
                            <div className={`shipping-radio ${isSelected ? 'checked' : ''}`}>
                              {isSelected && <div className="radio-inner"></div>}
                            </div>
                            <div className="shipping-provider-info">
                              <div className="provider-name-row">
                                <span className="provider-name">{rate.providerName}</span>
                                {isServiceable && rate.freeShipping && (
                                  <span className="free-badge">FREE DELIVERY</span>
                                )}
                              </div>
                              {isServiceable && (
                                <span className="provider-tagline">Fast & Reliable Delivery</span>
                              )}
                            </div>
                          </div>

                          {isServiceable ? (
                            <div className="shipping-card-right">
                              <div className="delivery-date-box">
                                <span className="delivery-label">Expected Delivery</span>
                                <span className="delivery-date">{formatDate(rate.expectedDeliveryDate)}</span>
                                <span className="delivery-days-badge">{rate.estimatedDays} days</span>
                              </div>
                              <div className="shipping-cost-box">
                                {rate.freeShipping ? (
                                  <>
                                    <span className="cost-free">FREE</span>
                                    {rate.originalCost > 0 && (
                                      <span className="cost-original">₹{rate.originalCost}</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="cost-amount">₹{rate.shippingCost}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="shipping-card-right">
                              <span className="not-available">Not available for this pincode</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            <motion.div
              className="form-section"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="section-title">Payment</h2>
              <div className="payment-methods">
                <div className="payment-method active">
                  <div className="payment-method-icon">💳</div>
                  <div>
                    <div className="payment-method-name">Razorpay</div>
                    <div className="payment-method-desc">
                      Cards, UPI, NetBanking, Wallets
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="checkout-summary"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="summary-title">Order Summary</h2>

            <div className="order-items">
              {items.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="order-item-info">
                    <div className="order-item-name">
                      {item.baseName || 'Custom'} × {item.shadeName || 'Lamp'}
                    </div>
                    <div className="order-item-specs">
                      {item.baseColorName || item.baseColor} base, {item.shadeColorName || item.shadeColor} shade
                    </div>
                  </div>
                  <div className="order-item-qty">×{item.quantity}</div>
                  <div className="order-item-price">
                    ₹{((item.price || 0) * item.quantity).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {selectedAddress && (
              <div className="delivery-preview">
                <h4>Delivering to</h4>
                <p className="delivery-name">{selectedAddress.fullName}</p>
                <p className="delivery-address">
                  {selectedAddress.addressLine1}
                  {selectedAddress.addressLine2 && `, ${selectedAddress.addressLine2}`}
                </p>
                <p className="delivery-location">
                  {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                </p>
              </div>
            )}

            <CouponInput
              subtotal={subtotal}
              shippingCost={shippingCost}
              onCouponApplied={handleCouponApplied}
              onCouponRemoved={handleCouponRemoved}
            />

            <div className="summary-divider"></div>

            <div className="summary-row">
              <span>Subtotal ({items.length} items)</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {!selectedAddress ? (
                  <span className="shipping-pending">Select address</span>
                ) : shippingLoading ? (
                  <span className="shipping-pending">Calculating...</span>
                ) : selectedRate?.freeShipping || couponFreeDelivery ? (
                  <span className="free-label">
                    FREE
                    {couponFreeDelivery && shippingCost > 0 && (
                      <span className="shipping-original-price"> ₹{shippingCost.toLocaleString()}</span>
                    )}
                  </span>
                ) : selectedRate ? (
                  `₹${shippingCost.toLocaleString()}`
                ) : (
                  <span className="shipping-pending">Select provider</span>
                )}
              </span>
            </div>
            {discount > 0 && (
              <div className="summary-row discount-row">
                <span>Coupon Discount</span>
                <span className="discount-amount">-₹{discount.toLocaleString()}</span>
              </div>
            )}

            <div className="summary-divider"></div>

            <div className="summary-row total">
              <span>Total</span>
              <span>₹{total.toLocaleString()}</span>
            </div>

            {selectedRate && (
              <div className="delivery-estimate">
                Expected delivery: {formatDate(selectedRate.expectedDeliveryDate)}
              </div>
            )}

            <motion.button
              className="pay-button"
              onClick={handlePayment}
              disabled={!canProceed || isLoading}
              whileHover={{ scale: canProceed && !isLoading ? 1.02 : 1 }}
              whileTap={{ scale: canProceed && !isLoading ? 0.98 : 1 }}
            >
              {isLoading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  Processing...
                </>
              ) : (
                `Pay ₹${total.toLocaleString()}`
              )}
            </motion.button>

            <p className="secure-payment-note">
              🔒 Secure payment powered by Razorpay
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
